import React, { useState, useEffect } from 'react';
import { FileText, Download, Eye, Loader2 } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

/**
 * DocumentsSection — uses files prop from RPC get_public_tracking.
 * Only shows file_type = 'invoice' or 'document'. Photos are excluded.
 */
const DocumentsSection = ({ shipmentId, files }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDocuments = async () => {
      try {
        setLoading(true);

        if (!files || files?.length === 0) {
          setDocuments([]);
          return;
        }

        // ✅ Filter only invoice and document types — exclude photos
        const docFiles = files?.filter(f => f?.file_type === 'invoice' || f?.file_type === 'document');

        if (docFiles?.length === 0) {
          setDocuments([]);
          return;
        }

        // Generate signed URLs for each document from the RPC files array
        const docsWithUrls = await Promise.all(
          docFiles?.map(async (doc) => {
            if (!doc?.file_url) return { ...doc, downloadUrl: null };

            if (doc?.file_url?.startsWith('http://') || doc?.file_url?.startsWith('https://')) {
              return { ...doc, downloadUrl: doc?.file_url };
            }

            const bucketsToTry = [
              doc?.bucket_name,
              'shipment-documents',
              'invoices',
              'shipment-photos',
            ]?.filter(Boolean);

            const uniqueBuckets = [...new Set(bucketsToTry)];

            for (const bucket of uniqueBuckets) {
              const { data: urlData, error: urlError } = await supabase?.storage?.from(bucket)?.createSignedUrl(doc?.file_url, 3600);
              if (!urlError && urlData?.signedUrl) {
                return { ...doc, downloadUrl: urlData?.signedUrl, resolvedBucket: bucket };
              }
            }

            return { ...doc, downloadUrl: null };
          })
        );

        setDocuments(docsWithUrls);
      } catch (error) {
        console.error('Error loading documents:', error);
        setDocuments([]);
      } finally {
        setLoading(false);
      }
    };

    loadDocuments();
  }, [files]);

  const getFileTypeLabel = (fileType) => {
    const labels = {
      invoice: 'Facture',
      document: 'Document',
      main: 'Document principal',
      douane: 'Douane',
      cni: 'CNI',
      autorisation: 'Autorisation',
      autre: 'Autre',
    };
    return labels?.[fileType] || fileType || 'Document';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const mb = bytes / (1024 * 1024);
    return mb < 1 ? `${(bytes / 1024)?.toFixed(1)} KB` : `${mb?.toFixed(1)} MB`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr)?.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const handleView = (doc) => {
    if (doc?.downloadUrl) {
      window.open(doc?.downloadUrl, '_blank');
    }
  };

  const handleDownload = (doc) => {
    if (doc?.downloadUrl) {
      const a = document.createElement('a');
      a.href = doc?.downloadUrl;
      a.download = doc?.file_name || 'document';
      a.target = '_blank';
      document.body?.appendChild(a);
      a?.click();
      document.body?.removeChild(a);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center h-32">
          <Loader2 className="w-8 h-8 animate-spin text-green-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center mb-4">
        <FileText className="w-5 h-5 text-green-700 mr-2" />
        <h2 className="text-lg font-semibold text-gray-900">Documents</h2>
        {documents?.length > 0 && (
          <span className="ml-auto text-sm text-gray-500">{documents?.length} document{documents?.length > 1 ? 's' : ''}</span>
        )}
      </div>
      {documents?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <FileText className="w-16 h-16 mb-3" />
          <p className="text-sm">Aucun document disponible</p>
          <p className="text-xs mt-1">Les documents seront affichés ici une fois téléversés</p>
        </div>
      ) : (
        <div className="space-y-3">
          {documents?.map((doc) => (
            <div
              key={doc?.id}
              className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center flex-1 min-w-0">
                <div className="flex-shrink-0 w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-red-600" />
                </div>
                <div className="ml-4 flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {doc?.file_name || 'Document'}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-gray-500">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {getFileTypeLabel(doc?.file_type)}
                    </span>
                    {doc?.file_size && <span>{formatFileSize(doc?.file_size)}</span>}
                    {doc?.created_at && <span>{formatDate(doc?.created_at)}</span>}
                  </div>
                </div>
              </div>
              <div className="ml-4 flex-shrink-0 flex items-center gap-2">
                <button
                  onClick={() => handleView(doc)}
                  disabled={!doc?.downloadUrl}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Voir
                </button>
                <button
                  onClick={() => handleDownload(doc)}
                  disabled={!doc?.downloadUrl}
                  className="inline-flex items-center px-3 py-2 border border-green-600 rounded-md shadow-sm text-sm font-medium text-green-700 bg-white hover:bg-green-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Télécharger
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DocumentsSection;